/**
 * Custom hook and store for Botpress Webchat integration.
 * Manages global chat state (open/close) and user context synchronization.
 */
"use client";

import { useAuth } from "@/hooks/use-auth";
import { usePathname } from "next/navigation";
import { useCallback } from "react";

export interface BotPressUserData {
	name: string | undefined;
	pictureUrl: string | undefined;
	data: {
		email: string | undefined;
		phone: string | undefined;
		id: string | undefined;
		url: string;
		pathname: string;
		pageSection: string;
	};
}

export interface WindowBotPress {
	getUserData: () => BotPressUserData;
	open: () => void;
	close: () => void;
	sendEvent: (event: { type: string; payload?: Record<string, unknown> }) => void;
	sendMessage: (message: string) => void;
	updateUser: (userData: BotPressUserData) => void;
	on: (event: string, callback: (data?: unknown) => void) => void;
}

// Extend Window interface for Botpress
declare global {
	interface Window {
		botpress?: WindowBotPress;
	}
}

// const CLIENT_ID = "b4c43c7e-02a6-494b-97d6-db755806e449";

function getPageSection(pathname: string): string {
	const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);

	if (segments.length === 0) return "home";

	const firstSegment = segments[0];

	if (firstSegment === "contact") return "contact";
	if (firstSegment === "sell") return "seller";
	if (firstSegment === "about") return "about";
	if (firstSegment === "faq" || firstSegment === "faqs") return "faq";
	if (firstSegment === "blog") return "blog";
	if (firstSegment === "privacy" || firstSegment === "terms") return "legal";

	const cityPatterns = ["bengaluru", "bangalore", "mumbai", "delhi", "hyderabad", "pune", "chennai"];
	if (cityPatterns.includes(firstSegment.toLowerCase())) {
		const secondSegment = segments[1];
		if (secondSegment === "units" || secondSegment === "unit") return "property_listing";
		if (secondSegment === "projects" || secondSegment === "project") return "project_listing";
		if (secondSegment === "search") return "search";
		return "city_browse";
	}

	if (firstSegment === "dashboard") {
		const secondSegment = segments[1];
		if (secondSegment === "buyer") return "buyer_dashboard";
		if (secondSegment === "seller") return "seller_dashboard";
		return "dashboard";
	}

	if (firstSegment === "preferences" || firstSegment === "shortlist") return "buyer_preferences";
	if (firstSegment === "compare") return "property_comparison";

	return firstSegment;
}

/**
 * Hook to control the Botpress webchat state
 * Used with the React library components
 */

export interface UseBotpressWebchatReturn extends WindowBotPress {
	isOpen: boolean;
	isInitialized: boolean;
	isSecondScriptLoaded: boolean;
	isFirstScriptInjected: boolean;
	setIsFirstScriptInjected: (loaded: boolean) => void;
	setIsSecondScriptLoaded: (loaded: boolean) => void;
	setIsOpen: (isOpen: boolean) => void;
	setIsInitialized: (isInitialized: boolean) => void;
}

import { create } from "zustand";

interface BotpressStore {
	isOpen: boolean;
	isInitialized: boolean;
	isSecondScriptLoaded: boolean;
	isFirstScriptInjected: boolean;
	setIsFirstScriptInjected: (loaded: boolean) => void;
	setIsSecondScriptLoaded: (loaded: boolean) => void;
	setIsOpen: (isOpen: boolean) => void;
	setIsInitialized: (isInitialized: boolean) => void;
}

export const useBotpressStore = create<BotpressStore>(set => ({
	isOpen: false,
	isInitialized: false,
	isSecondScriptLoaded: false,
	isFirstScriptInjected: false,
	setIsFirstScriptInjected: loaded => set({ isFirstScriptInjected: loaded }),
	setIsSecondScriptLoaded: loaded => set({ isSecondScriptLoaded: loaded }),
	setIsOpen: isOpen => set({ isOpen }),
	setIsInitialized: isInitialized => set({ isInitialized }),
}));

export function useBotpressWebchat(): UseBotpressWebchatReturn {
	const store = useBotpressStore();
	const { user } = useAuth();
	const pathname = usePathname();

	// Helper to extract user email
	const getUserData = useCallback(() => {
		const url = typeof window !== "undefined" ? window.location.href : "";
		const data: BotPressUserData = {
			name: user?.name,
			pictureUrl: user?.profile?.image,
			data: {
				email: user?.emails?.[0]?.address,
				phone: user?.phones?.[0]?.number,
				id: user?.id,
				url,
				pathname,
				pageSection: getPageSection(pathname),
			},
		};
		return data;
	}, [user, pathname]);

	const open = useCallback(() => {
		if (window.botpress) {
			window.botpress.open();
			store.setIsOpen(true);
		}
	}, [store]);

	const close = useCallback(() => {
		if (window.botpress) {
			window.botpress.close();
			store.setIsOpen(false);
		}
	}, [store]);

	const sendEvent = useCallback((event: { type: string; payload?: Record<string, unknown> }) => {
		window.botpress?.sendEvent(event);
	}, []);

	const updateUser = useCallback((userData: BotPressUserData) => {
		window.botpress?.updateUser(userData);
	}, []);

	const sendMessage = useCallback((message: string) => {
		window.botpress?.sendMessage(message);
	}, []);

	const on = useCallback((event: string, callback: (data?: unknown) => void) => {
		window.botpress?.on(event, callback);
	}, []);

	return {
		...store,
		getUserData,
		open,
		close,
		sendEvent,
		sendMessage,
		updateUser,
		on,
	};
}
